import { z } from "zod";
import type {
  AlteracaoDeConta,
  BancoDoCatalogo,
  ContaDaPessoa,
  ContaNova,
} from "@/api/contrato";

/*
 * A conta no painel: as regras do catálogo para a tela, a validação e os corpos.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A TELA LÊ O CATÁLOGO, E NÃO O CÓDIGO DO BANCO.                           │
 * │                                                                          │
 * │ Nenhum `756` aqui: o que muda de um banco para outro vem de              │
 * │ `GET /admin/v1/bancos`, que é o `internal/domain/banco.go`. Um segundo   │
 * │ lugar com regra de banco é o que o catálogo existe para evitar.          │
 * │                                                                          │
 * │ A validação daqui espelha a da API para o erro aparecer no campo antes   │
 * │ da viagem, e não a substitui: quem decide é `ValidarContaContraOBanco`.  │
 * │ Por isso o tamanho vai no Zod, e não em `maxLength`: colar um número     │
 * │ maior seria cortado em silêncio, e conta errada é o erro mais caro aqui. │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

export const TIPOS_DE_CONTA = [
  ["corrente", "Corrente"],
  ["poupanca", "Poupança"],
] as const;

export function rotuloDoTipo(tipo: string | undefined): string {
  return TIPOS_DE_CONTA.find(([valor]) => valor === tipo)?.[1] ?? "—";
}

/** `3021` e `5` viram `3021-5`, sem traço solto na conta sem dígito. */
export function comDigito(numero: string, digito: string | undefined): string {
  return digito ? `${numero}-${digito}` : numero;
}

/*
 * O sinal ausente vale `false`: a tela trava em vez de prometer uma alteração que a
 * API pode recusar. O tipo é opcional aqui de propósito, para a comparação com `true`
 * continuar existindo mesmo com o contrato dizendo que o campo sempre vem.
 */
export function janelaAberta(
  conta: Partial<Pick<ContaDaPessoa, "alteracao_bancaria_permitida">>,
): boolean {
  return conta.alteracao_bancaria_permitida === true;
}

export const DICA_DA_JANELA =
  "Já há boleto com estes dados. A correção é feita no banco e com a TecnoSpeed.";

/** `undefined` em banco com ficha: nenhum rótulo precisa de aviso. */
export function dicaDoBanco(banco: BancoDoCatalogo | undefined): string | undefined {
  return banco !== undefined && !banco.tem_perfil_de_ficha ? "Este banco não gera PDF." : undefined;
}

/** O catálogo manda `""` no banco sem orientação, e `Dica` vazia seria um tooltip em branco. */
export function dicaDoCatalogo(orientacao: string | undefined): string | undefined {
  return orientacao === undefined || orientacao === "" ? undefined : orientacao;
}

/* ── Regras de exibição ──────────────────────────────────────────────────── */

type UsoDeDV = NonNullable<BancoDoCatalogo["dv_da_agencia"]>;

/** `max` zero é **não declarado**, e não "zero dígitos", como no catálogo. */
export interface FormatoDeDigitos {
  min: number;
  max: number;
}

export interface RegrasDaConta {
  rotuloDaAgencia: string;
  dvDaAgencia: UsoDeDV;
  dvDaConta: UsoDeDV;
  aceitaBancoCorrespondente: boolean;
  agencia: FormatoDeDigitos;
  conta: FormatoDeDigitos;
  codigoBeneficiario: FormatoDeDigitos;
}

/**
 * O que o formulário mostra e confere para um banco.
 *
 * `undefined` é banco que a tela não achou no catálogo: nada é escondido nem conferido,
 * porque esconder campo sem regra faria `corpoDaEdicao` apagar o que está gravado.
 */
export function regrasDoBanco(banco: BancoDoCatalogo | undefined): RegrasDaConta {
  return {
    // Sem DV de agência, o posto entra na própria agência (`DVNaoUsado`).
    rotuloDaAgencia: banco?.dv_da_agencia === "nao_usado" ? "Agência e posto" : "Agência",
    dvDaAgencia: banco?.dv_da_agencia ?? "",
    dvDaConta: banco?.dv_da_conta ?? "",
    aceitaBancoCorrespondente: banco === undefined || banco.aceita_banco_correspondente === true,
    agencia: { min: banco?.agencia_min ?? 0, max: banco?.agencia_max ?? 0 },
    conta: { min: banco?.conta_min ?? 0, max: banco?.conta_max ?? 0 },
    codigoBeneficiario: {
      min: banco?.codigo_beneficiario_min ?? 0,
      max: banco?.codigo_beneficiario_max ?? 0,
    },
  };
}

export function exigeCodigoBeneficiario(regras: RegrasDaConta): boolean {
  return regras.codigoBeneficiario.max > 0;
}

/* ── Formulário ──────────────────────────────────────────────────────────── */

/*
 * As chaves são os nomes da API, como em `plano.ts`: a recusa do servidor aponta o
 * campo por esse nome, e uma tradução no meio seria um mapa para esquecer.
 */
export interface ValoresDaConta {
  banco: string;
  tipo: string;
  agencia: string;
  agencia_dv: string;
  conta: string;
  conta_dv: string;
  codigo_beneficiario: string;
  banco_correspondente: string;
}

export type CampoDaConta = keyof ValoresDaConta;
export type ErrosDaConta = Partial<Record<CampoDaConta, string | undefined>>;

/** O código do beneficiário nasce vazio: a dica do Sicredi pode não valer para a conta. */
export const VALORES_INICIAIS: ValoresDaConta = {
  banco: "",
  tipo: "corrente",
  agencia: "",
  agencia_dv: "",
  conta: "",
  conta_dv: "",
  codigo_beneficiario: "",
  banco_correspondente: "",
};

/** A conta gravada, como o formulário a mostra para edição. */
export function valoresDe(conta: ContaDaPessoa): ValoresDaConta {
  return {
    banco: conta.banco,
    tipo: conta.tipo ?? "corrente",
    agencia: conta.agencia,
    agencia_dv: conta.agencia_dv ?? "",
    conta: conta.conta,
    conta_dv: conta.conta_dv ?? "",
    codigo_beneficiario: conta.codigo_beneficiario ?? "",
    banco_correspondente: conta.banco_correspondente ?? "",
  };
}

/** Os campos que o formulário mostra AGORA, para `errosDoFormulario` não perder recusa. */
export function camposDaConta(regras: RegrasDaConta): readonly CampoDaConta[] {
  return [
    "banco",
    "tipo",
    "agencia",
    "conta",
    "codigo_beneficiario",
    ...(regras.dvDaAgencia === "nao_usado" ? [] : (["agencia_dv"] as const)),
    ...(regras.dvDaConta === "nao_usado" ? [] : (["conta_dv"] as const)),
    ...(regras.aceitaBancoCorrespondente ? (["banco_correspondente"] as const) : []),
  ];
}

const OBRIGATORIO = "Obrigatório.";
const SO_DIGITOS = "Só dígitos.";

function textoDoTamanho({ min, max }: FormatoDeDigitos): string {
  if (min === max) return `${max} dígitos.`;
  return min > 1 ? `De ${min} a ${max} dígitos.` : `Até ${max} dígitos.`;
}

/** A mesma conta de `conferirTamanho`, em `banco.go`. */
function defeitoDosDigitos(
  valor: string,
  formato: FormatoDeDigitos,
  obrigatorio: boolean,
): string | null {
  if (valor === "") return obrigatorio ? OBRIGATORIO : null;
  if (!/^\d+$/.test(valor)) return SO_DIGITOS;
  if (formato.max === 0) return null;
  return valor.length > formato.max || valor.length < formato.min
    ? textoDoTamanho(formato)
    : null;
}

function digitos(formato: FormatoDeDigitos, obrigatorio: boolean) {
  return z
    .string()
    .trim()
    .superRefine((valor, contexto) => {
      const defeito = defeitoDosDigitos(valor, formato, obrigatorio);
      if (defeito !== null) contexto.addIssue({ code: "custom", message: defeito });
    });
}

function digitoVerificador(uso: UsoDeDV) {
  return uso === "obrigatorio" ? z.string().trim().min(1, OBRIGATORIO) : z.string().trim();
}

function esquemaDaConta(regras: RegrasDaConta) {
  return z.object({
    banco: z.string().min(1, "Escolha o banco."),
    tipo: z.enum(["corrente", "poupanca"]),
    agencia: digitos(regras.agencia, true),
    agencia_dv: digitoVerificador(regras.dvDaAgencia),
    conta: digitos(regras.conta, true),
    conta_dv: digitoVerificador(regras.dvDaConta),
    codigo_beneficiario: digitos(regras.codigoBeneficiario, exigeCodigoBeneficiario(regras)),
    // Aqui só o formato: quem confere se o banco está no catálogo é a API.
    banco_correspondente: z
      .string()
      .trim()
      .refine((valor) => valor === "" || /^\d{3}$/.test(valor), "3 dígitos."),
  });
}

export type ContaValidada = z.output<ReturnType<typeof esquemaDaConta>>;

export type ResultadoDaValidacao =
  | { ok: true; conta: ContaValidada }
  | { ok: false; erros: ErrosDaConta };

/*
 * O que o banco não usa sai antes da validação, e continua no estado do formulário:
 * trocar de Sicoob para Sicredi com o DV digitado levaria `422 campo_nao_aplicavel`
 * por um campo que o operador nem vê mais.
 */
function soOQueOBancoUsa(valores: ValoresDaConta, regras: RegrasDaConta): ValoresDaConta {
  return {
    ...valores,
    agencia_dv: regras.dvDaAgencia === "nao_usado" ? "" : valores.agencia_dv,
    conta_dv: regras.dvDaConta === "nao_usado" ? "" : valores.conta_dv,
    banco_correspondente: regras.aceitaBancoCorrespondente ? valores.banco_correspondente : "",
  };
}

export function validarConta(
  valores: ValoresDaConta,
  regras: RegrasDaConta,
): ResultadoDaValidacao {
  const resultado = esquemaDaConta(regras).safeParse(soOQueOBancoUsa(valores, regras));
  if (resultado.success) return { ok: true, conta: resultado.data };

  const erros: ErrosDaConta = {};
  for (const problema of resultado.error.issues) {
    const campo = problema.path[0];
    if (typeof campo === "string" && campo in valores && erros[campo as CampoDaConta] === undefined) {
      erros[campo as CampoDaConta] = problema.message;
    }
  }
  return { ok: false, erros };
}

/** O corpo da criação: só o preenchido. A pessoa vai na rota, e nunca aqui. */
export function corpoDaCriacao(conta: ContaValidada): ContaNova {
  return {
    banco: conta.banco,
    agencia: conta.agencia,
    conta: conta.conta,
    tipo: conta.tipo,
    ...(conta.agencia_dv !== "" && { agencia_dv: conta.agencia_dv }),
    ...(conta.conta_dv !== "" && { conta_dv: conta.conta_dv }),
    ...(conta.codigo_beneficiario !== "" && { codigo_beneficiario: conta.codigo_beneficiario }),
    ...(conta.banco_correspondente !== "" && {
      banco_correspondente: conta.banco_correspondente,
    }),
  };
}

const CAMPOS_DE_TEXTO = [
  "agencia",
  "agencia_dv",
  "conta",
  "conta_dv",
  "codigo_beneficiario",
  "banco_correspondente",
] as const;

/**
 * O corpo da edição: só o que MUDOU.
 *
 * Todo campo aqui toca a janela e o catálogo, e um `PATCH` com o valor antigo seria
 * recusado com a janela fechada por nada. Vazio limpa o opcional, e por isso conta
 * legada com DV que o banco não usa corrige ao salvar: o campo sumiu da tela, e a
 * pendência `conta_incompleta` não teria saída.
 */
export function corpoDaEdicao(antes: ContaDaPessoa, depois: ContaValidada): AlteracaoDeConta {
  const corpo: AlteracaoDeConta = {};

  for (const campo of CAMPOS_DE_TEXTO) {
    if (depois[campo] !== (antes[campo] ?? "")) corpo[campo] = depois[campo];
  }
  if (depois.tipo !== (antes.tipo ?? "corrente")) corpo.tipo = depois.tipo;

  return corpo;
}
